import {
  QueryClient,
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { warehouseApi } from 'src/api/warehouse';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import {
  CreateWarehouseAdjustmentDto,
  WarehouseDocumentMutationResponseDto,
  WarehouseProductDetailDto,
} from 'woofwoof-api';
import {
  WarehouseProductFormValues,
  buildCreateProductPayload,
  buildProductFields,
} from '../components/products/warehouseProductForm.schema';
import { warehouseErrorCode } from '../utils/errors';
import {
  requireOrganizationId,
  warehouseRequestOptions,
} from '../utils/request';

/**
 * Every stock or catalogue mutation refreshes the product list and detail
 * (prefix match, so every organization-scoped key is covered), the
 * unassigned-issue lists, which a shortfall or a batch assignment changes,
 * and the medicine search.
 */
export function invalidateWarehouseStock(queryClient: QueryClient): void {
  void queryClient.invalidateQueries(['warehouse-products']);
  void queryClient.invalidateQueries(['warehouse-product']);
  void queryClient.invalidateQueries(['warehouse-unassigned']);
  // #690: the medication form's medicine search shows each Product's stock.
  void queryClient.invalidateQueries(['warehouse-medicine-search']);
}

/**
 * Creates or updates the product for the selected organization (S3): the
 * request always names it explicitly, per notes-1d.md's fix-round-1 forms.
 */
export function useSaveWarehouseProduct(
  productId?: string,
): UseMutationResult<
  WarehouseProductDetailDto,
  unknown,
  WarehouseProductFormValues
> {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganizationContext();
  const organizationId = selectedOrganization?.id;
  return useMutation({
    mutationFn: async (values: WarehouseProductFormValues) =>
      (productId
        ? await warehouseApi.warehouseProductControllerUpdate(
            {
              id: productId,
              updateWarehouseProductDto: buildProductFields(values),
            },
            warehouseRequestOptions(requireOrganizationId(organizationId)),
          )
        : await warehouseApi.warehouseProductControllerCreate(
            { createWarehouseProductDto: buildCreateProductPayload(values) },
            warehouseRequestOptions(requireOrganizationId(organizationId)),
          )
      ).data,
    onSuccess: () => invalidateWarehouseStock(queryClient),
    // FW-2/4: a 422 UNIT_LOCKED means the product's Unit changed elsewhere
    // since this form read it; invalidate so the refetched product shows the
    // lock instead of letting the form retry blind.
    onError: (error) => {
      if (warehouseErrorCode(error) === 'UNIT_LOCKED') {
        void queryClient.invalidateQueries(['warehouse-product']);
      }
    },
  });
}

export function useArchiveWarehouseProduct(
  productId: string,
): UseMutationResult<WarehouseProductDetailDto, unknown, void> {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganizationContext();
  const organizationId = selectedOrganization?.id;
  return useMutation({
    mutationFn: async () =>
      (
        await warehouseApi.warehouseProductControllerArchive(
          { id: productId },
          warehouseRequestOptions(requireOrganizationId(organizationId)),
        )
      ).data,
    onSuccess: () => invalidateWarehouseStock(queryClient),
  });
}

/**
 * A KOR and the organization of the form that built it (FW-1/4): both are
 * pinned together, so a retry never goes to another organization.
 */
export interface CreateAdjustmentVariables {
  organizationId: string | undefined;
  dto: CreateWarehouseAdjustmentDto;
}

/**
 * "Korekta stanu" (D6, R2-4): the operation key travels in the payload
 * (`buildAdjustmentPayload`); the request names the organization pinned with
 * it (S3, FW-1/4), not the one selected when it leaves.
 */
export function useCreateWarehouseAdjustment(): UseMutationResult<
  WarehouseDocumentMutationResponseDto,
  unknown,
  CreateAdjustmentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ organizationId, dto }: CreateAdjustmentVariables) =>
      (
        await warehouseApi.warehouseDocumentControllerCreateAdjustment(
          { createWarehouseAdjustmentDto: dto },
          warehouseRequestOptions(requireOrganizationId(organizationId)),
        )
      ).data,
    // An ambiguous answer may hide a committed KOR (FW-1/5).
    onSettled: () => {
      invalidateWarehouseStock(queryClient);
      void queryClient.invalidateQueries(['warehouse-documents']);
    },
  });
}
